import { memo, useMemo } from 'react';
import {
  Alert,
  Button,
  SpaceBetween,
  Tabs,
  TabsProps,
  TextContent,
} from '@amzn/awsui-components-core';

import { AttachmentsTabView } from './AttachmentsTabView';
import { AuthorsTabView } from './AuthorsTabView';
import { CodeRepoTabView } from './CodeRepoTabView';
import ContentTitleHeadingView from './ContentTitleHeadingView';
import ContentsTabView from './ContentsTabView';
import { HistoryTabView } from './HistoryTabView';
import { MetadataTabView } from './MetadataTabView';
import { ContentEditorPageLoadedViewModel } from './ContentEditorPageViewModel';
import { isTabId, useCurrentTabId, useNavigateToTab } from '../v2-hooks/useUrlHash';
import { useAudience } from '../v2-hooks/useAudience';
import { useContent } from '../v2-hooks/useContent';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useCurrentUser } from '../../contexts/UserContext';
import { extractUserAlias } from '../../api/UtilsApi';
import { isLockedByOther } from '../../model/LoadedDocument';
import { validateCodeRepoAudience, validateCodeReview } from '../../api/ValidationUtil';
import constants from '../../common/constants';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

interface LoadedContentEditorProps {
  readonly vm: ContentEditorPageLoadedViewModel;
}

/** The content editor once its document has finished loading. Renders the title heading and tabbed editing surface. */
const LoadedContentEditor = memo(function LoadedContentEditor({ vm }: LoadedContentEditorProps) {
  const currentTabId = useCurrentTabId();
  const navigateToTab = useNavigateToTab();
  const content = useContent();
  const audience = useAudience();
  const service = useContentEditorService();
  const { userId } = useCurrentUser();
  const document = useLoadedDocument();

  const lockedByOther = document ? isLockedByOther(document, userId) : false;
  const lockHolderAlias =
    lockedByOther && document && document.kind !== 'publication'
      ? extractUserAlias(document.workflow.lock?.acquiredBy ?? '')
      : undefined;

  // When content or audience changes, recompute content-level validation alerts
  const contentAlerts = useMemo(() => {
    if (!content || !audience) return [];
    return [
      validateCodeReview(audience, content),
      validateCodeRepoAudience(audience, content),
    ].filter(Boolean);
  }, [content, audience]);

  const tabs: readonly TabsProps.Tab[] = [
    { label: 'Contents', id: 'contents', content: <ContentsTabView /> },
    { label: 'Metadata', id: 'metadata', content: <MetadataTabView /> },
    { label: 'Code', id: 'code', content: <CodeRepoTabView /> },
    { label: 'Attachments', id: 'attachments', content: <AttachmentsTabView /> },
    { label: 'Authors', id: 'authors', content: <AuthorsTabView /> },
    { label: 'History', id: 'history', content: <HistoryTabView /> },
  ];
  const activeTabId =
    currentTabId !== undefined && tabs.some((t) => t.id === currentTabId)
      ? currentTabId
      : // @ts-expect-error TS2532 - strict migration
        tabs[0].id;

  return (
    <SpaceBetween size="l">
      {vm.isSystemLocked && (
        <Alert
          type="info"
          action={
            <Button variant="normal" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          }
        >
          {constants.SYSTEM_LOCK_BANNER}
        </Alert>
      )}
      {lockedByOther && (
        <Alert
          type="warning"
          action={
            <Button variant="normal" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          }
        >
          Content is being locked by user <strong>{lockHolderAlias}</strong>. You will not be able
          to save your changes or start quality check until the user{' '}
          <strong>{lockHolderAlias}</strong> finish editing.
        </Alert>
      )}
      <ContentTitleHeadingView />
      {contentAlerts.length > 0 && (
        <Alert type="error" header="We found issues with your content">
          <TextContent>
            {constants.CODE_VALIDATION_ERROR_INTRO_TEXT}
            <ul>
              {contentAlerts.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          </TextContent>
        </Alert>
      )}
      <Tabs
        activeTabId={activeTabId}
        onChange={({ detail }) => {
          if (!isTabId(detail.activeTabId)) {
            throw new Error(`Unknown tab id: ${detail.activeTabId}`);
          }
          navigateToTab(detail.activeTabId);
        }}
        tabs={tabs}
      />
    </SpaceBetween>
  );
});

export default LoadedContentEditor;
